<template>
  <section class="section-container section-container-wider">
    <div class="section-container-header-box fade-up-animation">
      <p class="list-text">Services</p>
    </div>
    <h1 class="section-container-heading">
      <p
        v-for="line in headingLines"
        :key="line.text"
        class="extra-large-text-list fade-up-animation"
        :class="{ 'special-text': line.special }">
        {{ line.text }}
      </p>
    </h1>
  </section>

  <section class="section-container section-container-wider">
    <div class="services">
      <article
        v-for="(service, index) in services"
        :id="service.slug"
        :key="service.slug"
        class="services-item">
        <aside class="services-item-aside">
          <p class="small-text-list text-uppercase fade-up-animation">
            {{ String(index + 1).padStart(2, '0') }}
          </p>
          <h2 class="heading-3 fade-up-animation">{{ service.name }}</h2>
          <p class="body-text fade-up-animation services-item-aside-tagline">{{ service.tagline }}</p>
        </aside>

        <div class="services-item-main">
          <div class="services-item-intro fade-up-animation">
            <p v-for="paragraph in service.intro" :key="paragraph" class="body-text">{{ paragraph }}</p>
          </div>

          <div class="services-item-capabilities">
            <div
              v-for="capability in service.capabilities"
              :key="capability.title"
              class="services-item-capabilities-item fade-up-animation">
              <h3 class="body-text-medium">{{ capability.title }}</h3>
              <p class="body-text">{{ capability.description }}</p>
            </div>
          </div>

          <div class="services-item-deliverables fade-up-animation">
            <p class="small-text-list text-uppercase">What you get</p>
            <ul class="services-item-deliverables-list">
              <li
                v-for="deliverable in service.deliverables"
                :key="deliverable"
                class="services-item-deliverables-item">
                <span class="services-item-deliverables-item-marker"></span>
                <p class="body-text-medium">{{ deliverable }}</p>
              </li>
            </ul>
          </div>
        </div>
      </article>
    </div>
  </section>

  <section class="section-container section-container-wider section-container-double-gap">
    <h2 class="section-container-heading">
      <p
        v-for="line in ctaLines"
        :key="line.text"
        class="extra-large-text-list fade-up-animation"
        :class="{ 'special-text': line.special }">
        {{ line.text }}
      </p>
    </h2>
    <nuxt-link class="arrow-button arrow-button-bigger fade-up-animation" to="/#contact" aria-label="Start a project">
      <p class="heading-4-bold">Start a project</p>
      <div class="arrow-button-icon" v-html="[icons.rightArrowIcon, icons.rightArrowIcon]"></div>
    </nuxt-link>
  </section>
</template>

<script lang="ts" setup>
import { useFadeUpObserver } from '@/composables/useFadeUpObserver';
import icons from '~/icons';
import { services } from '@/data/services';

const headingLines = [
  { text: 'What we', special: false },
  { text: 'do best.', special: true },
];

const ctaLines = [
  { text: 'Got a project', special: false },
  { text: 'in mind?', special: true },
  { text: "Let's build it.", special: false },
];

useFadeUpObserver();

useSeo({
  title: 'Services | VORTEC',
  description:
    'Web development, UI/UX design, support & maintenance and end-to-end product development — the services we offer at VORTEC.',
  path: '/services',
  jsonLd: [
    {
      '@type': 'ItemList',
      name: 'Services | VORTEC',
      url: 'https://vortec.io/services',
      itemListElement: services.map((service, index) => ({
        '@type': 'Service',
        position: index + 1,
        name: service.name,
        description: service.tagline,
        url: `https://vortec.io/services#${service.slug}`,
      })),
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://vortec.io' },
        { '@type': 'ListItem', position: 2, name: 'Services', item: 'https://vortec.io/services' },
      ],
    },
  ],
});
</script>
